import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courseService } from '../../services/courseService';
import { toast } from 'react-toastify';
import { STORAGE_BASE_URL } from '../../config/api';
import EmployeeBottomNavigation from '../../components/EmployeeBottomNavigation';
import Loader from '../../components/Loader';
import { useTranslation } from '../../hooks/useTranslation';
import notificationService from '../../services/notificationService';

const VideoLesson = () => {
    const { courseId, videoId } = useParams();
    const navigate = useNavigate();
    const { t, locale } = useTranslation();
    const [video, setVideo] = useState(null);
    const [course, setCourse] = useState(null);
    const [courseProgress, setCourseProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isCompleted, setIsCompleted] = useState(false);
    const [isCompleting, setIsCompleting] = useState(false);
    const [showCompletionModal, setShowCompletionModal] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    useEffect(() => {
        loadVideo();
        loadCourse();
        loadCourseProgress();
    }, [courseId, videoId]);

    const handleBack = () => {
        navigate(-1);
    };

    const loadCourse = async () => {
        try {
            const response = await courseService.getById(courseId);
            if (response.success) {
                setCourse(response.data);
            }
        } catch (error) {
            console.error('Error loading course:', error);
        }
    };

    const loadCourseProgress = async () => {
        try {
            const response = await courseService.getCourseProgress(courseId);
            if (response.success) {
                setCourseProgress(response.data);
            }
        } catch (error) {
            // Course might not be started yet, that's okay
            console.log('Course not started yet or error loading progress:', error);
        }
    };

    const loadVideo = async () => {
        try {
            setLoading(true);
            // Get course progress to find video details
            const progressResponse = await courseService.getCourseProgress(courseId);
            if (progressResponse.success) {
                const videos = progressResponse.data.videos || [];
                const foundVideo = videos.find(v => v.id === parseInt(videoId));
                if (foundVideo) {
                    setVideo(foundVideo);
                    setIsCompleted(foundVideo.is_completed || false);
                } else {
                    // If not found in progress, try to get from course
                    const courseResponse = await courseService.getById(courseId);
                    if (courseResponse.success && courseResponse.data.videos) {
                        const foundVideo = courseResponse.data.videos.find(v => v.id === parseInt(videoId));
                        if (foundVideo) {
                            setVideo(foundVideo);
                            setIsCompleted(false);
                        }
                    }
                }
            } else {
                // If course not started, get from course data
                const courseResponse = await courseService.getById(courseId);
                if (courseResponse.success && courseResponse.data.videos) {
                    const foundVideo = courseResponse.data.videos.find(v => v.id === parseInt(videoId));
                    if (foundVideo) {
                        setVideo(foundVideo);
                        setIsCompleted(false);
                    }
                }
            }
        } catch (error) {
            toast.error(t('employee.videoLesson.loadFailed') || 'Failed to load video');
            navigate(`/employee/courses/${courseId}`);
        } finally {
            setLoading(false);
        }
    };

    const handleStartVideo = async () => {
        try {
            await courseService.startVideo(parseInt(videoId));
        } catch (error) {
            console.error('Error starting video:', error);
        }
    };

    const handleCompleteVideo = async () => {
        try {
            setIsCompleting(true);
            const response = await courseService.endVideo(parseInt(videoId));
            if (response.success) {
                setIsCompleted(true);
                toast.success(t('employee.videoLesson.videoCompleted') || 'Video completed successfully!');

                // Reload course progress to get updated data
                await loadCourseProgress();

                // Check if course is completed
                const isCourseCompleted = response.data?.is_course_completed || false;

                if (isCourseCompleted) {
                    // Show completion modal
                    setTimeout(() => {
                        setShowCompletionModal(true);
                    }, 500);
                } else {
                    // Find next video
                    const updatedProgress = await courseService.getCourseProgress(courseId);
                    if (updatedProgress.success) {
                        const videos = updatedProgress.data.videos || [];
                        const currentVideoIndex = videos.findIndex(v => v.id === parseInt(videoId));
                        const nextVideo = videos[currentVideoIndex + 1];

                        if (nextVideo && !nextVideo.is_completed) {
                            // Navigate to next video
                            setTimeout(() => {
                                navigate(`/employee/courses/${courseId}/videos/${nextVideo.id}`);
                            }, 1000); // Small delay to show success message
                        } else {
                            // No next video or all videos completed, show completion modal
                            setTimeout(() => {
                                setShowCompletionModal(true);
                            }, 500);
                        }
                    }
                }
            }
        } catch (error) {
            toast.error(error.response?.data?.message || t('employee.videoLesson.completeFailed') || 'Failed to complete video');
        } finally {
            setIsCompleting(false);
        }
    };
    useEffect(() => {
        loadUnreadCount();
    }, []);

    const loadUnreadCount = async () => {
        const response = await notificationService.getUnreadCount();
        if (response.success) {
            setUnreadCount(response.count);
        }
    };

    useEffect(() => {
        if (video && !isCompleted) {
            handleStartVideo();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [video]);

    if (loading) {
        return <Loader fullScreen={true} size="large" />;
    }

    if (!video) {
        return (
            <div className="video-lesson-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                <div className="text-center py-4">
                    {t('employee.videoLesson.videoNotFound') || 'Video not found'}
                </div>
            </div>
        );
    }

    return (
        <div className="video-lesson-page" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
            {/* Header with Navigation */}
            <div className="settings-header d-flex justify-content-between align-items-center px-3 py-3" dir='ltr'>
                <div className="notification-icon">
                <button
                onClick={() => navigate('/employee/notifications')}
                style={{
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: 'pointer',
                    position: 'relative'
                }}
            >
                <span className="bell-icon">🔔</span>
                {unreadCount > 0 && (
                    <span
                        style={{
                            position: 'absolute',
                            top: '-2px',
                            right: '-2px',
                            background: '#dc2626',
                            color: 'white',
                            borderRadius: '50%',
                            width: '18px',
                            height: '18px',
                            fontSize: '11px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold'
                        }}
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>
                </div>
                <div className="settings-title" style={{ fontSize: '16px', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {video.title}
                </div>
                <button className="settings-back-btn" onClick={handleBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#343330" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </button>
            </div>

            {/* Video Player Section */}
            <div className="video-player-section px-3 mb-3">
                <div className="video-container mb-3" style={{
                    width: '100%',
                    aspectRatio: '16/9',
                    backgroundColor: '#000',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    position: 'relative'
                }}>
                    {video.video_url ? (
                        <iframe
                            src={video.video_url}
                            title={video.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none'
                            }}
                        />
                    ) : (
                        <div style={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            flexDirection: 'column',
                            gap: '16px'
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                            </svg>
                            <p style={{ fontSize: '16px', color: '#999' }}>
                                {t('employee.videoLesson.noVideoUrl') || 'Video URL not available'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Video Title */}
                <h1 className="video-title-lesson mb-2" style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>
                    {video.title}
                </h1>

                {/* Video Description */}
                {video.description && (
                    <div className="video-description-lesson mb-3" style={{
                        fontSize: '14px',
                        color: '#6c757d',
                        lineHeight: '1.6',
                        marginBottom: '16px'
                    }}>
                        <span dangerouslySetInnerHTML={{ __html: video.description }} />
                    </div>
                )}

                {/* Course Info */}
                {course && (
                    <div className="course-info-badge mb-3" style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 12px',
                        backgroundColor: '#f0f4ff',
                        borderRadius: '8px',
                        fontSize: '14px',
                        color: 'rgb(91 137 203)',
                        fontWeight: '500'
                    }}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                            <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
                        </svg>
                        <span>{course.name}</span>
                    </div>
                )}
            </div>

            {/* Complete Video Button */}
            <div className="video-action-section px-3 mb-5">
                {isCompleted ? (
                    <button
                        className="btn-video-completed"
                        disabled
                        style={{
                            width: '100%',
                            padding: '14px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: 'not-allowed',
                            opacity: 0.8,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        {t('employee.videoLesson.alreadyCompleted') || 'Video Completed'}
                    </button>
                ) : (
                    <button
                        className="btn-complete-video"
                        onClick={handleCompleteVideo}
                        disabled={isCompleting}
                        style={{
                            width: '100%',
                            padding: '14px',
                            backgroundColor: 'rgb(91 137 203)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            fontSize: '16px',
                            fontWeight: '600',
                            cursor: isCompleting ? 'not-allowed' : 'pointer',
                            opacity: isCompleting ? 0.7 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            transition: 'opacity 0.2s ease'
                        }}
                    >
                        {isCompleting ? (
                            <>
                                <Loader size="small" />
                                <span>{t('employee.videoLesson.completing') || 'Completing...'}</span>
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                                <span>{t('employee.videoLesson.markAsCompleted') || 'Mark as Completed'}</span>
                            </>
                        )}
                    </button>
                )}
            </div>

            {/* Navigation to Course */}
            <div className="back-to-course px-3 mb-5">
                <button
                    className="btn-back-to-course"
                    onClick={() => navigate(`/employee/courses/${courseId}`)}
                    style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: 'transparent',
                        color: 'rgb(91 137 203)',
                        border: '1px solid rgb(91 137 203)',
                        borderRadius: '8px',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px'
                    }}
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"></polyline>
                    </svg>
                    {t('employee.videoLesson.backToCourse') || 'Back to Course'}
                </button>
            </div>

            {/* Course Completion Modal */}
            {showCompletionModal && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                        padding: '20px'
                    }}
                    onClick={() => setShowCompletionModal(false)}
                >
                    <div
                        style={{
                            backgroundColor: 'white',
                            borderRadius: '16px',
                            padding: '32px',
                            maxWidth: '400px',
                            width: '100%',
                            textAlign: 'center',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            backgroundColor: '#28a745',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 24px',
                            animation: 'scaleIn 0.3s ease'
                        }}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                        </div>
                        <h2 style={{
                            fontSize: '24px',
                            fontWeight: '700',
                            marginBottom: '12px',
                            color: '#212529'
                        }}>
                            {t('employee.videoLesson.courseCompleted') || 'Course Completed!'}
                        </h2>
                        <p style={{
                            fontSize: '16px',
                            color: '#6c757d',
                            marginBottom: '24px',
                            lineHeight: '1.5'
                        }}>
                            {t('employee.videoLesson.courseCompletedMessage') || 'Congratulations! You have successfully completed this course.'}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <button
                                onClick={() => navigate(`/employee/courses/${courseId}`)}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    backgroundColor: 'rgb(91 137 203)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                {t('employee.videoLesson.viewCourse') || 'View Course'}
                            </button>
                            <button
                                onClick={() => navigate('/employee/home')}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    backgroundColor: 'transparent',
                                    color: 'rgb(91 137 203)',
                                    border: '1px solid rgb(91 137 203)',
                                    borderRadius: '8px',
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    cursor: 'pointer'
                                }}
                            >
                                {t('employee.videoLesson.backToHome') || 'Back to Home'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Bottom Navigation */}
            <EmployeeBottomNavigation />
        </div>
    );
};

export default VideoLesson;

